import React from "react";
import { Link } from "react-router-dom";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialMedia = [
    {
      name: "Facebook",
      icon: (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
            clipRule="evenodd"
          />
        </svg>
      ),
      url: "https://www.facebook.com/profile.php?id=61577778472218",
    },
    {
      name: "Twitter",
      icon: (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
        </svg>
      ),
      url: "https://x.com/quikresize",
    },
    {
      name: "YouTube",
      icon: (
        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z"
            clipRule="evenodd"
          />
        </svg>
      ),
      url: "https://www.youtube.com/@QuikResize",
    },
  ];

  const footerSections = [
    {
      title: "Tools",
      links: [
        {
          name: "Online Image Resizer & Optimizer",
          to: "/image-resizer",
          description: "Resize, compress, crop and optimize images for web and social media",
        },
        {
          name: "Free PDF Converter & Compressor",
          to: "/pdf-resizer",
          description: "Convert, merge, split, and compress PDF files easily online",
        },
        {
          name: "Multilingual Text-to-Speech & Translator",
          to: "/text-to-speech",
          description: "Convert text to voice and translate into 100+ languages",
        },
      ],
    },
    {
      title: "Company",
      links: [
        { name: "About Us", to: "/about-us", description: "Learn about our mission and values" },
        { name: "Contact", to: "/contact-us", description: "Reach out to our support team" },
        { name: "FAQ", to: "/faq", description: "Answers to frequently asked questions" },
        { name: "Features", to: "/features", description: "Explore all QuikResize features" },
        { name: "Sitemap", to: "/sitemap", description: "Complete list of all pages and tools" },
      ],
    },
    {
      title: "Legal",
      links: [
        { name: "Privacy Policy", to: "/privacy-policy", description: "Understand how we protect data" },
        { name: "Terms of Service", to: "/terms-of-service", description: "Review our service terms" },
        { name: "Cookie Policy", to: "/cookie-policy", description: "Cookie usage and preferences" },
        { name: "DMCA", to: "/dmca", description: "Copyright and takedown information" },
      ],
    },
  ];

  return (
    <footer className="bg-gray-900 text-white" aria-labelledby="footer-heading">
      <h2 id="footer-heading" className="sr-only">Footer</h2>
      <div className="container mx-auto px-4 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-12">
          <div className="lg:col-span-2 space-y-6">
            <Link to="/" className="flex items-center">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                QuikResize
              </h3>
            </Link>
            <p className="text-gray-400 text-sm md:text-base">
              QuikResize provides secure, online tools to compress images, convert PDFs,
              resize media, and translate text into 100+ languages. Trusted by students,
              professionals, and creators worldwide.
            </p>

            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
                Get our mobile app
              </h4>
              <div className="flex flex-col sm:flex-row gap-2">
                <a
                  href="https://play.google.com/store/apps/details?id=com.yourcompany.yourapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-md hover:from-blue-600 hover:to-purple-700 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 text-sm font-medium text-center"
                  title="Download QuikResize Android App"
                >
                  Android App
                </a>
                <a
                  href="https://apps.apple.com/us/app/your-app-name/id1234567890"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-md hover:from-blue-600 hover:to-purple-700 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 text-sm font-medium text-center"
                  title="Download QuikResize iOS App"
                >
                  iOS App
                </a>
              </div>
            </div>
          </div>

          {footerSections.map((section, index) => (
            <nav key={index} aria-label={section.title}>
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-100 relative pb-2 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-10 after:h-0.5 after:bg-gradient-to-r after:from-blue-400 after:to-purple-600">
                  {section.title}
                </h3>
                <ul className="space-y-3">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <Link
                        to={link.to}
                        title={link.name}
                        aria-label={link.description}
                        className="text-gray-400 hover:text-white transition-colors duration-300 group flex flex-col"
                      >
                        <span className="group-hover:pl-2 transition-all duration-300">
                          {link.name}
                        </span>
                        <span className="text-xs text-gray-500 group-hover:text-gray-400 transition-colors duration-300">
                          {link.description}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </nav>
          ))}
        </div>

        <div className="border-t border-gray-800 my-8 lg:my-10"></div>

        <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <p className="text-gray-400 text-sm">
              &copy; {currentYear} QuikResize. All rights reserved.
            </p>
            <div className="flex gap-4">
              <Link to="/privacy-policy" title="Privacy Policy" className="text-gray-400 hover:text-white text-sm">Privacy</Link>
              <Link to="/terms-of-service" title="Terms of Service" className="text-gray-400 hover:text-white text-sm">Terms</Link>
              <Link to="/cookie-policy" title="Cookie Policy" className="text-gray-400 hover:text-white text-sm">Cookies</Link>
              <Link to="/sitemap" title="Sitemap" className="text-gray-400 hover:text-white text-sm">Sitemap</Link>
            </div>
          </div>

          <div className="flex space-x-6">
            {socialMedia.map((social, index) => (
              <a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors duration-300 transform hover:-translate-y-1 hover:scale-110"
                aria-label={`Follow us on ${social.name}`}
                title={`Follow QuikResize on ${social.name}`}
              >
                <span className="sr-only">{social.name}</span>
                {social.icon}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;